import React, { useEffect } from 'react';
import { Box, Tooltip } from '@mui/material';
import bgSpotifyLight from '../assets/images/backgrounds/spotify_light.png';
import bgSpotifyDark from '../assets/images/backgrounds/spotify_dark.png';
import bgAppleLight from '../assets/images/backgrounds/apple_light.png';
import bgAppleDark from '../assets/images/backgrounds/apple_dark.png';
import moon from '../assets/images/moon.svg';
import bgBtn from '../assets/images/btn.png';
import mowpodCharts from '../assets/images/mowpod-charts.png';
import mowpodChartsLight from '../assets/images/mowpod-charts-light.png';
import { Search } from '../components/Search/Search';
import { EmailNotify } from '../components/EmailNotify/EmailNotify';
import { PodcastInfo } from '../components/PodcastInfo/PodcastInfo';
import { useStyles, ThemeSwitch } from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { PodcastCharts } from '../components/PodcastCharts/PodcastCharts';
import Chart from '../components/Chart/Chart';
import { Footer } from '../components/Footer/Footer';
import { useAutoDemo } from '../hooks/useAutoDemo';
import {
  fetchCountries,
  fetchGenres,
  fetchTopPodcasts,
  resetGenres,
  resetTopPodcasts,
} from '../redux/slices/podcastsSlice';
import { BrowseCharts } from '../components/BrowsePodcasts/BrowsePodcasts';
import { toggleTheme } from '../redux/slices/themeSlice';
import { useSearchParams } from 'react-router-dom';
import { updatePodcastUrl } from '../utils/updatePodcastUrl';
import { PlatformSwitch } from '../components/PlatformSwitch/PlatformSwitch';
import { Analyze13 } from '../components/Analyze13/Analyze13';

export const Charts = () => {
  const {
    currentPodcast,
    startDate,
    endDate,
    showBrowseGrid,
    browseMode,
    favorites,
    shouldFetchTopPodcasts,
    activePlatform,
    demoModePodcasts,
    showAnalyze,
  } = useSelector((state) => state.podcasts);
  const { isLight } = useSelector((state) => state.theme);

  const getBackground = () => {
    if (activePlatform === 'spotify') {
      return isLight ? bgSpotifyLight : bgSpotifyDark;
    } else {
      return isLight ? bgAppleLight : bgAppleDark;
    }
  };

  const classes = useStyles({ bg: getBackground(), bgBtn });
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const hasUrlParams = searchParams.toString().length > 0;

  useAutoDemo();

  useEffect(() => {
    dispatch(fetchGenres());
    dispatch(fetchCountries());
  }, [dispatch, activePlatform]);

  useEffect(() => {
    const shouldSkip = !favorites.length && !shouldFetchTopPodcasts && !hasUrlParams;

    if (shouldSkip) {
      return;
    }

    if (currentPodcast) {
      dispatch(
        fetchTopPodcasts({
          podcastId: currentPodcast.collectionId,
          name: currentPodcast.collectionName,
          artist: currentPodcast.artistName,
          startDate,
          endDate,
          feedUrl: currentPodcast.feedUrl,
        })
      );
    }
  }, [currentPodcast, dispatch, startDate, endDate, shouldFetchTopPodcasts, hasUrlParams]);

  useEffect(() => {
    const fetchData = async () => {
      if (currentPodcast) {
        try {
          dispatch(resetTopPodcasts());
          dispatch(resetGenres());
          const response = await dispatch(
            fetchTopPodcasts({
              name: currentPodcast.collectionName,
              artist: currentPodcast.artistName,
              startDate,
              endDate,
              feedUrl: currentPodcast.feedUrl,
            })
          ).unwrap();
          if (!demoModePodcasts.length) {
            const spotifyId = currentPodcast?.spotifyUrl?.split('/').pop();
            updatePodcastUrl(response.topPodcasts[0]?.podcastId || spotifyId || currentPodcast.collectionId);
          }
        } catch (error) {
          console.error('Failed to fetch top podcasts:', error);
        }
      }
    };

    fetchData();
  }, [activePlatform]);

  const handleToggleTheme = () => {
    dispatch(toggleTheme());
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.logoWrapper}>
        <Box className={classes.logoContainer}>
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = '/';
            }}
            className={classes.logoLink}
          >
            <img
              src={isLight ? mowpodChartsLight : mowpodCharts}
              alt="Mowpod Charts"
              className={classes.logo}
            />
          </a>

          <Box className={classes.switchesContainer}>
            <PlatformSwitch />
            <Tooltip title={isLight ? 'Switch to Dark Theme' : 'Switch to Light Theme'}>
              <ThemeSwitch
                checked={isLight}
                onChange={handleToggleTheme}
                moonIcon={moon}
                inputProps={{ 'aria-label': 'Theme Switch' }}
              />
            </Tooltip>
          </Box>
        </Box>
      </Box>
      <Box className={classes.header}>
        <Box className={classes.headerTop}>
          <Search />
          {!browseMode && <EmailNotify />}
        </Box>
        <PodcastInfo />
        <Box className={classes.divider} />
      </Box>
      <>
        {showBrowseGrid && <BrowseCharts />}
        {showAnalyze ? (
          <Box className={classes.header}><Analyze13 /></Box>
        ) : (
          <>
            <Chart />
            <Box className={classes.header}>{currentPodcast && <PodcastCharts />}</Box>
          </>
        )}
      </>
      <Footer />
    </Box>
  );
};
